import { useMemo, type ReactNode } from 'react';
import { Card } from '../../components/ui';
import { t } from '../../i18n';
import type {
  MydataPeriodSummary,
  MydataYearSummary,
} from '../../types/mydata';
import { formatMoney } from '../../utils/format';
import { formatVatCodeLabel, vatCodeGroup, vatCodeSide } from './vatCodes';

type ChartSeries = {
  key: string;
  label: string;
  color: string;
  values: number[];
};

const COLORS = {
  income: '#0f766e',
  expense: '#be123c',
  vatOut: '#0369a1',
  vatIn: '#7c3aed',
  net: '#334155',
  previous: '#94a3b8',
  current: '#0f172a',
  output: '#0f766e',
  outputVat: '#0369a1',
  input: '#a16207',
  inputVat: '#7c3aed',
  adjustment: '#64748b',
  other: '#475569',
};

function toNum(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function formatAxis(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}εκ.`;
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}χιλ.`;
  }
  return String(Math.round(value));
}

function ChartLegend({ series }: { series: ChartSeries[] }) {
  return (
    <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function ChartHeading({
  title,
  hint,
  trailing,
}: {
  title: string;
  hint: string;
  trailing?: ReactNode;
}) {
  return (
    <div className="mb-3 space-y-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {trailing}
      </div>
      <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>
    </div>
  );
}

function ComparisonBarChart({
  categories,
  series,
  ariaLabel,
  compactLabels = false,
}: {
  categories: string[];
  series: ChartSeries[];
  ariaLabel: string;
  compactLabels?: boolean;
}) {
  const width = categories.length > 6 ? 720 : 640;
  const height = 240;
  const pad = { top: 12, right: 12, bottom: compactLabels ? 36 : 40, left: 52 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const maxValue = niceMax(Math.max(...series.flatMap((s) => s.values), 0));
  const groupCount = Math.max(categories.length, 1);
  const groupW = plotW / groupCount;
  const barGap = 3;
  const barW = Math.max(
    6,
    (groupW - 10 - barGap * (series.length - 1)) / series.length,
  );
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxValue);

  return (
    <div>
      <ChartLegend series={series} />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={ariaLabel}
      >
        {ticks.map((tick) => {
          const y = pad.top + plotH - (tick / maxValue) * plotH;
          return (
            <g key={tick}>
              <line
                x1={pad.left}
                x2={pad.left + plotW}
                y1={y}
                y2={y}
                className="stroke-slate-200 dark:stroke-slate-700"
                strokeWidth={1}
              />
              <text
                x={pad.left - 8}
                y={y + 3}
                textAnchor="end"
                className="fill-slate-500 dark:fill-slate-400"
                fontSize={10}
              >
                {formatAxis(tick)}
              </text>
            </g>
          );
        })}
        {categories.map((label, index) => {
          const groupX = pad.left + index * groupW + 5;
          return (
            <g key={`${label}-${index}`}>
              {series.map((s, seriesIndex) => {
                const value = Math.max(0, s.values[index] ?? 0);
                const barH = (value / maxValue) * plotH;
                const x = groupX + seriesIndex * (barW + barGap);
                const y = pad.top + plotH - barH;
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={y}
                    width={barW}
                    height={Math.max(barH, value > 0 ? 1 : 0)}
                    fill={s.color}
                    rx={2}
                  >
                    <title>
                      {label} — {s.label}: {formatMoney(value)}
                    </title>
                  </rect>
                );
              })}
              <text
                x={groupX + (series.length * (barW + barGap) - barGap) / 2}
                y={height - 14}
                textAnchor="middle"
                className="fill-slate-500 dark:fill-slate-400"
                fontSize={compactLabels ? 9 : 10}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function HorizontalBars({
  rows,
  ariaLabel,
}: {
  rows: Array<{ key: string; label: string; value: number; color: string }>;
  ariaLabel: string;
}) {
  const maxValue = niceMax(Math.max(...rows.map((r) => Math.abs(r.value)), 0));
  return (
    <div className="space-y-3" role="img" aria-label={ariaLabel}>
      {rows.map((row) => {
        const widthPct = Math.max(
          0,
          Math.min(100, (Math.abs(row.value) / maxValue) * 100),
        );
        return (
          <div key={row.key}>
            <div className="mb-1 flex items-start justify-between gap-3 text-xs">
              <span className="min-w-0 text-slate-600 dark:text-slate-300">
                {row.label}
              </span>
              <span className="shrink-0 tabular-nums text-slate-800 dark:text-slate-100">
                {formatMoney(row.value)}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full rounded-full"
                style={{ width: `${widthPct}%`, backgroundColor: row.color }}
                title={`${row.label}: ${formatMoney(row.value)}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MydataCharts({
  summary,
  yearSummary,
}: {
  summary: MydataPeriodSummary | null;
  yearSummary?: MydataYearSummary | null;
}) {
  const labels = t().mydata;
  const monthNamesShort = labels.monthNamesShort;
  const previous = summary?.previous ?? null;

  const monthlyCategories = useMemo(
    () => monthNamesShort.slice(0, 12),
    [monthNamesShort],
  );

  const monthlySeries = useMemo<ChartSeries[]>(() => {
    const months =
      yearSummary?.months ??
      Array.from({ length: 12 }, (_, i) => ({
        month: i + 1,
        synced: false,
        incomeNet: '0',
        expenseNet: '0',
        netResult: '0',
      }));

    const byMonth = new Map(months.map((m) => [m.month, m]));
    const income: number[] = [];
    const expense: number[] = [];
    const net: number[] = [];
    for (let m = 1; m <= 12; m += 1) {
      const row = byMonth.get(m);
      income.push(row?.synced ? Math.max(0, toNum(row.incomeNet)) : 0);
      expense.push(row?.synced ? Math.max(0, toNum(row.expenseNet)) : 0);
      net.push(row?.synced ? Math.max(0, toNum(row.netResult)) : 0);
    }

    return [
      {
        key: 'income',
        label: labels.incomeNet,
        color: COLORS.income,
        values: income,
      },
      {
        key: 'expense',
        label: labels.expenseNet,
        color: COLORS.expense,
        values: expense,
      },
      {
        key: 'net',
        label: labels.netResult,
        color: COLORS.net,
        values: net,
      },
    ];
  }, [
    labels.expenseNet,
    labels.incomeNet,
    labels.netResult,
    yearSummary?.months,
  ]);

  const hasMonthlyData = useMemo(
    () =>
      (yearSummary?.months ?? []).some(
        (m) => m.synced && (toNum(m.incomeNet) !== 0 || toNum(m.expenseNet) !== 0),
      ),
    [yearSummary?.months],
  );

  const comparisonCategories = useMemo(
    () => [
      labels.incomeNet,
      labels.expenseNet,
      labels.netResult,
      labels.vatBalance,
    ],
    [labels.expenseNet, labels.incomeNet, labels.netResult, labels.vatBalance],
  );

  const comparisonSeries = useMemo<ChartSeries[]>(() => {
    if (!summary) return [];
    const currentValues = [
      toNum(summary.incomeNet),
      toNum(summary.expenseNet),
      toNum(summary.netResult),
      toNum(summary.vatPayable),
    ];
    const series: ChartSeries[] = [
      {
        key: 'current',
        label: labels.chartCurrentPeriod,
        color: COLORS.current,
        values: currentValues.map((v) => Math.max(0, v)),
      },
    ];
    if (previous) {
      series.push({
        key: 'previous',
        label: labels.chartPreviousPeriod,
        color: COLORS.previous,
        values: [
          toNum(previous.incomeNet),
          toNum(previous.expenseNet),
          toNum(previous.netResult),
          toNum(previous.vatPayable),
        ].map((v) => Math.max(0, v)),
      });
    }
    return series;
  }, [labels.chartCurrentPeriod, labels.chartPreviousPeriod, previous, summary]);

  const mixRows = useMemo(() => {
    if (!summary) return [];
    return [
      {
        key: 'income',
        label: labels.incomeNet,
        value: toNum(summary.incomeNet),
        color: COLORS.income,
      },
      {
        key: 'expense',
        label: labels.expenseNet,
        value: toNum(summary.expenseNet),
        color: COLORS.expense,
      },
      {
        key: 'outputVat',
        label: labels.outputVat,
        value: toNum(summary.outputVat),
        color: COLORS.vatOut,
      },
      {
        key: 'inputVat',
        label: labels.inputVat,
        value: toNum(summary.inputVat),
        color: COLORS.vatIn,
      },
    ];
  }, [
    labels.expenseNet,
    labels.incomeNet,
    labels.inputVat,
    labels.outputVat,
    summary,
  ]);

  const vatCodeSections = useMemo(() => {
    const entries = Object.entries(summary?.vatCodes ?? {})
      .map(([code, amount]) => {
        const group = vatCodeGroup(code);
        const side = vatCodeSide(code);
        return {
          key: code,
          side,
          label: formatVatCodeLabel(code, labels),
          value: Number(amount) || 0,
          color: COLORS[group],
        };
      })
      .filter((row) => row.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    const bySide = (side: ReturnType<typeof vatCodeSide>) =>
      entries.filter((row) => row.side === side).slice(0, 10);

    return {
      sales: bySide('sales'),
      purchases: bySide('purchases'),
      adjustments: bySide('adjustment'),
      other: bySide('other'),
    };
  }, [labels, summary?.vatCodes]);

  const hasVatCodes =
    vatCodeSections.sales.length > 0 ||
    vatCodeSections.purchases.length > 0 ||
    vatCodeSections.adjustments.length > 0 ||
    vatCodeSections.other.length > 0;

  const hasPeriodCharts = Boolean(summary?.synced);

  if (!hasPeriodCharts && !hasMonthlyData) return null;

  return (
    <section className="shrink-0 space-y-3">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-ink">{labels.sectionCharts}</h2>
        <p className="text-xs text-ink-faint">{labels.sectionChartsHint}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5 lg:col-span-2">
          <ChartHeading
            title={labels.chartMonthlyTitle}
            hint={labels.chartMonthlyHint}
            trailing={
              yearSummary?.year ? (
                <span className="text-sm text-ink-faint">{yearSummary.year}</span>
              ) : null
            }
          />
          {hasMonthlyData ? (
            <ComparisonBarChart
              categories={monthlyCategories}
              series={monthlySeries}
              ariaLabel={`${labels.chartMonthlyTitle} ${yearSummary?.year ?? ''}`}
              compactLabels
            />
          ) : (
            <p className="text-sm text-ink-faint">{labels.chartMonthlyEmpty}</p>
          )}
        </Card>

        {hasPeriodCharts ? (
          <>
            <Card className="p-5">
              <ChartHeading
                title={labels.chartComparisonTitle}
                hint={labels.chartComparisonHint}
              />
              <ComparisonBarChart
                categories={comparisonCategories}
                series={comparisonSeries}
                ariaLabel={labels.chartComparisonTitle}
              />
            </Card>

            <Card className="p-5">
              <ChartHeading
                title={labels.chartMixTitle}
                hint={labels.chartMixHint}
              />
              <HorizontalBars rows={mixRows} ariaLabel={labels.chartMixTitle} />
            </Card>
          </>
        ) : null}

        {hasVatCodes ? (
          <>
            <div className="space-y-1 lg:col-span-2">
              <h3 className="text-sm font-semibold text-ink">
                {labels.chartVatCodesTitle}
              </h3>
              <p className="text-xs leading-relaxed text-ink-faint">
                {labels.chartVatCodesHint}
              </p>
            </div>
            {vatCodeSections.sales.length > 0 ? (
              <Card className="p-5">
                <ChartHeading
                  title={labels.vatCodesSalesTitle}
                  hint={labels.vatCodesSalesHint}
                />
                <HorizontalBars
                  rows={vatCodeSections.sales}
                  ariaLabel={labels.vatCodesSalesTitle}
                />
              </Card>
            ) : null}
            {vatCodeSections.purchases.length > 0 ? (
              <Card className="p-5">
                <ChartHeading
                  title={labels.vatCodesPurchasesTitle}
                  hint={labels.vatCodesPurchasesHint}
                />
                <HorizontalBars
                  rows={vatCodeSections.purchases}
                  ariaLabel={labels.vatCodesPurchasesTitle}
                />
              </Card>
            ) : null}
            {vatCodeSections.adjustments.length > 0 ? (
              <Card className="p-5">
                <ChartHeading
                  title={labels.vatCodesAdjustmentsTitle}
                  hint={labels.vatCodesAdjustmentsHint}
                />
                <HorizontalBars
                  rows={vatCodeSections.adjustments}
                  ariaLabel={labels.vatCodesAdjustmentsTitle}
                />
              </Card>
            ) : null}
            {vatCodeSections.other.length > 0 ? (
              <Card className="p-5">
                <ChartHeading
                  title={labels.vatCodesOtherTitle}
                  hint={labels.vatCodesOtherHint}
                />
                <HorizontalBars
                  rows={vatCodeSections.other}
                  ariaLabel={labels.vatCodesOtherTitle}
                />
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
